// Aceite de termos, gate de `403 terms_not_accepted` (`spec/17-frontend.md` §8.1).
// Esta tela só chama rotas que rodam com `skipTerms`: `GET /v1/me/terms` (API-24) e
// `POST /v1/me/terms/accept`. Qualquer rota guardada devolveria o mesmo 403 aqui e a tela
// se redirecionaria em laço.
//
// A versão NÃO é mais constante no front: quem manda é `TERMS_VERSION` da API. Com a
// constante duplicada, subir a versão no servidor deixava o painel aceitando a antiga -
// o `POST` passava e o gate reprovava no request seguinte, em laço.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { api } from '../api/client.ts';
import { errorMessageOf } from '../api/errors.ts';
import type { paths } from '../api/types/public.d.ts';
import { FormError } from '../components/app/auth-shell.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardContent } from '../components/ui/card.tsx';

type TermsResponse = paths['/v1/me/terms']['get']['responses'][200]['content']['application/json'];

function Terms() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const terms = useQuery({
    queryKey: ['terms'],
    queryFn: () => api<TermsResponse>('/v1/me/terms'),
    staleTime: 5 * 60_000,
  });
  const version = terms.data?.version;

  const accept = useMutation({
    mutationFn: async (accepted: string) =>
      api('/v1/me/terms/accept', { method: 'POST', body: { version: accepted } }),
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: '/app' });
    },
  });

  return (
    <>
      <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.04em] text-balance lg:text-[32px]">
        Termos de uso
      </h1>
      {version !== undefined && <p className="text-14 text-muted-foreground">Versão {version}</p>}
      {/* Cartão de leitura: coluna estreita (texto corrido não atravessa a tela) e o aceite
          no rodapé com fio, que é a única ação da página. */}
      <Card className="rise-in max-w-2xl gap-0 py-0">
        <CardContent className="flex flex-col gap-3 py-5">
          <p className="text-15 leading-relaxed text-foreground">
            {terms.data?.summary ??
              'Para usar o painel é preciso aceitar os termos de uso e a política de privacidade vigentes. O aceite fica registrado na sua conta com data e versão.'}
          </p>
          {terms.isError && <FormError message={errorMessageOf(terms.error)} />}
          {accept.isError && <FormError message={errorMessageOf(accept.error)} />}
        </CardContent>
        <div className="flex justify-end border-border border-t px-5 py-4">
          <Button
            className="w-full sm:w-auto"
            type="button"
            onClick={() => {
              if (version !== undefined) {
                accept.mutate(version);
              }
            }}
            disabled={accept.isPending || version === undefined}
          >
            {accept.isPending ? 'Registrando…' : 'Aceitar e continuar'}
          </Button>
        </div>
      </Card>
    </>
  );
}

export const Route = createFileRoute('/app/terms')({ component: Terms });
